import { useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";
import { api } from "../lib/api";
import { ChevronLeft, ChevronRight, CalendarDays, Coffee, Video, Users, UserRound, Pill, ClipboardCheck } from "lucide-react";

const toMin = (t) => { const [h, m] = String(t || "9:00").split(":").map(Number); return (h || 9) * 60 + (m || 0); };

const TYPE_STYLES = [
  [/telehealth/i, { bg: "bg-[#EDE7F6]", bar: "bg-[#6A1B9A]", text: "text-[#4A148C]", icon: Video }],
  [/group/i, { bg: "bg-[#E8F5E9]", bar: "bg-[#2E7D32]", text: "text-[#1B5E20]", icon: Users }],
  [/family/i, { bg: "bg-[#FFF3E0]", bar: "bg-[#E65100]", text: "text-[#BF360C]", icon: Users }],
  [/intake|assessment/i, { bg: "bg-[#E0F2F1]", bar: "bg-[#00695C]", text: "text-[#004D40]", icon: ClipboardCheck }],
  [/medication|psychiatric/i, { bg: "bg-[#FFEBEE]", bar: "bg-[#C62828]", text: "text-[#B71C1C]", icon: Pill }],
  [/individual/i, { bg: "bg-[#E3F2FD]", bar: "bg-[#1565C0]", text: "text-[#0D47A1]", icon: UserRound }],
];
const DEFAULT_STYLE = { bg: "bg-[#F5F2EB]", bar: "bg-[#6D4C41]", text: "text-[#4E342E]", icon: CalendarDays };
const styleFor = (type) => (TYPE_STYLES.find(([rx]) => rx.test(type || "")) || [null, DEFAULT_STYLE])[1];

const PX = 0.8;

export function MiniCalendar({ navigate }) {
  const [day, setDay] = useState(() => dayjs().format("YYYY-MM-DD"));
  const [appts, setAppts] = useState([]);
  const [nowTick, setNowTick] = useState(() => Date.now());

  useEffect(() => {
    api.get("/appointments", { params: { start: day, end: day } })
      .then((r) => setAppts(r.data.filter((a) => a.date === day && !["cancelled", "rescheduled"].includes(a.status))))
      .catch(() => setAppts([]));
  }, [day]);
  useEffect(() => { const t = setInterval(() => setNowTick(Date.now()), 60000); return () => clearInterval(t); }, []);

  const events = useMemo(() => {
    const rows = appts.map((a) => {
      const startM = toMin(a.start_time);
      const endM = a.end_time ? Math.max(toMin(a.end_time), startM + 25) : startM + 50;
      return { ...a, startM, endM };
    }).sort((a, b) => a.startM - b.startM || a.endM - b.endM);
    const laneEnds = [];
    rows.forEach((e) => {
      let lane = laneEnds.findIndex((end) => end <= e.startM);
      if (lane === -1) { lane = laneEnds.length; laneEnds.push(0); }
      laneEnds[lane] = e.endM;
      e.lane = lane;
    });
    const lanes = Math.max(1, laneEnds.length);
    rows.forEach((e) => { e.lanes = lanes; });
    return rows;
  }, [appts]);

  const isToday = day === dayjs().format("YYYY-MM-DD");
  const startHour = Math.min(8, ...events.map((e) => Math.floor(e.startM / 60)));
  const endHour = Math.max(18, ...events.map((e) => Math.ceil(e.endM / 60)));
  const height = (endHour - startHour) * 60 * PX;
  const now = dayjs(nowTick);
  const nowM = now.hour() * 60 + now.minute();
  const showNow = isToday && nowM >= startHour * 60 && nowM <= endHour * 60;
  const dayLabel = isToday ? "Today" : day === dayjs().add(1, "day").format("YYYY-MM-DD") ? "Tomorrow"
    : day === dayjs().subtract(1, "day").format("YYYY-MM-DD") ? "Yesterday" : dayjs(day).format("ddd, MMM D");

  return (
    <div data-testid="mini-calendar">
      <div className="mb-2 flex items-center justify-between">
        <button data-testid="minical-prev" aria-label="Previous day" onClick={() => setDay((d) => dayjs(d).subtract(1, "day").format("YYYY-MM-DD"))}
          className="flex h-7 w-7 items-center justify-center rounded-full text-[#7B6157] hover:bg-[#F5F2EB]"><ChevronLeft size={15} /></button>
        <div className="text-center">
          <div className="font-heading text-sm font-bold text-[#3E2723]" data-testid="minical-day-label">{dayLabel}</div>
          <div className="text-[10px] font-bold uppercase tracking-wide text-[#84675B]">{dayjs(day).format("MMMM D, YYYY")} · {events.length} appt{events.length === 1 ? "" : "s"}</div>
        </div>
        <div className="flex items-center gap-1">
          {!isToday && (
            <button data-testid="minical-today-btn" onClick={() => setDay(dayjs().format("YYYY-MM-DD"))}
              className="rounded-full bg-[#FFB300] px-2 py-1 text-[9px] font-bold uppercase text-[#3E2723] hover:bg-[#FFC933]">Today</button>
          )}
          <button data-testid="minical-next" aria-label="Next day" onClick={() => setDay((d) => dayjs(d).add(1, "day").format("YYYY-MM-DD"))}
            className="flex h-7 w-7 items-center justify-center rounded-full text-[#7B6157] hover:bg-[#F5F2EB]"><ChevronRight size={15} /></button>
        </div>
      </div>

      {events.length === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-xl bg-[#FDFCF8] py-10 text-center" data-testid="minical-empty">
          <Coffee size={22} className="mb-2 text-[#84675B]" />
          <p className="text-xs font-semibold text-[#7B6157]">Nothing scheduled {dayLabel === "Today" ? "today" : `on ${dayjs(day).format("MMM D")}`}</p>
        </div>
      ) : (
        <div className="relative" style={{ height }} data-testid="minical-timeline">
          {Array.from({ length: endHour - startHour + 1 }, (_, i) => startHour + i).map((h) => (
            <div key={h} className="absolute left-0 right-0 flex items-start gap-1.5" style={{ top: (h - startHour) * 60 * PX }}>
              <span className="w-10 shrink-0 -translate-y-1.5 text-right text-[9px] font-bold tabular-nums text-[#84675B]">
                {h === 12 ? "12 PM" : h > 12 ? `${h - 12} PM` : `${h} AM`}
              </span>
              <span className="mt-0 h-px flex-1 bg-[#F1EEE7]" />
            </div>
          ))}
          {events.map((e) => {
            const s = styleFor(e.type);
            const top = (e.startM - startHour * 60) * PX;
            const h = Math.max((e.endM - e.startM) * PX, 26);
            const width = `calc((100% - 3rem) / ${e.lanes})`;
            const left = `calc(3rem + (100% - 3rem) * ${e.lane} / ${e.lanes})`;
            const missed = e.status === "missed";
            const checkedIn = e.status === "checked_in";
            return (
              <button key={e.id} data-testid={`minical-appt-${e.id}`} onClick={() => navigate("/scheduler")}
                className={`absolute overflow-hidden rounded-lg text-left shadow-sm transition-all hover:z-10 hover:shadow-md hover:brightness-[0.98] ${s.bg} ${missed ? "opacity-50" : ""}`}
                style={{ top, height: h, left, width }}>
                <span className={`absolute left-0 top-0 h-full w-1 ${s.bar}`} />
                <span className="block px-2 py-1 pl-3">
                  <span className={`flex items-center gap-1 text-[10px] font-bold tabular-nums ${s.text}`}>
                    <s.icon size={10} className="shrink-0" /> {e.start_time}{e.end_time ? ` - ${e.end_time}` : ""}
                    {checkedIn && <span className="ml-auto rounded-full bg-white/70 px-1.5 text-[8px] uppercase">In</span>}
                    {missed && <span className="ml-auto rounded-full bg-white/70 px-1.5 text-[8px] uppercase">Missed</span>}
                  </span>
                  <span className={`block truncate text-[11px] font-semibold ${s.text} ${missed ? "line-through" : ""}`}>{e.client_name}</span>
                  {h >= 48 && <span className="block truncate text-[9px] text-[#7B6157]">{e.type}</span>}
                </span>
              </button>
            );
          })}
          {showNow && (
            <div className="pointer-events-none absolute left-10 right-0 z-20 flex items-center" style={{ top: (nowM - startHour * 60) * PX }} data-testid="minical-now-line">
              <span className="relative flex h-2.5 w-2.5 -translate-x-1">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#C62828] opacity-60" />
                <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-[#C62828]" />
              </span>
              <span className="h-[2px] flex-1 rounded bg-[#C62828]" />
            </div>
          )}
        </div>
      )}

      <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 border-t border-[#F1EEE7] pt-2" data-testid="minical-legend">
        {[["Individual", "bg-[#1565C0]"], ["Group", "bg-[#2E7D32]"], ["Telehealth", "bg-[#6A1B9A]"], ["Family", "bg-[#E65100]"], ["Intake", "bg-[#00695C]"], ["Medication", "bg-[#C62828]"]].map(([l, c]) => (
          <span key={l} className="flex items-center gap-1 text-[9px] font-semibold text-[#7B6157]"><span className={`h-1.5 w-1.5 rounded-full ${c}`} /> {l}</span>
        ))}
      </div>
    </div>
  );
}
